import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormArray, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import moment from 'moment';
import { BackOfficeService } from '../../../../../services/backoffice.service';

@Component({
  selector: 'app-route-schedule-override-create',
  templateUrl: './route-schedule-override-create.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class RouteScheduleOverrideCreateComponent {
  form: UntypedFormGroup;
  saving = false;
  error: string = null;

  // Templates (and existing overrides) for this route, offered as slot donors so nobody has to
  // hand-type the ReservedSeats/StartOffset JSON from scratch.
  donorTemplates: any[] = [];
  selectedDonorId: string = null;

  // Present when editing an existing override (vs. data.routeId, used for creating a new one).
  readonly editingTemplate: any;

  readonly typeOptions = [
    { value: 'Manual', label: 'One-off date', hint: 'Applies only on the exact date below.' },
    { value: 'DayOfYear', label: 'Every year on this date', hint: 'Recurs every year on this month/day (e.g. Dec 25).' },
    { value: 'DayOfWeek', label: 'Every week on this weekday', hint: 'Recurs every week on the weekday you pick.' },
  ];

  readonly weekdayOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  constructor(
    private fb: UntypedFormBuilder,
    private backofficeService: BackOfficeService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<RouteScheduleOverrideCreateComponent>,
  ) {
    this.donorTemplates = [...(this.data.routeTemplates || []), ...(this.data.routeOverrides || [])];
    this.editingTemplate = this.data.template || null;

    const slots = this.sortSlotsByTime(this.editingTemplate?.slots || []);
    const type = this.editingTemplate?.assignmentType || 'Manual';

    this.form = this.fb.group({
      name: [this.editingTemplate?.name || '', Validators.required],
      type: [type, Validators.required],
      date: [this.initialDateValue(type), type === 'DayOfWeek' ? [] : Validators.required],
      dayOfWeek: [type === 'DayOfWeek' ? this.editingTemplate.assignmentValue : 'Monday'],
      hasPeriod: [!!(this.editingTemplate?.assignmentDateFrom || this.editingTemplate?.assignmentDateTo)],
      dateFrom: [this.editingTemplate?.assignmentDateFrom || ''],
      dateTo: [this.editingTemplate?.assignmentDateTo || ''],
      slots: this.fb.array(slots.map((slot: any) => this.createSlotGroupFromData(slot)))
    });

    this.form.get('type').valueChanges.subscribe(newType => {
      const dateControl = this.form.get('date');
      if (newType === 'DayOfWeek') {
        dateControl.clearValidators();
      } else {
        dateControl.setValidators(Validators.required);
      }
      dateControl.updateValueAndValidity();
    });
  }

  // Manual/DayOfYear store their rule as an actual date; DayOfWeek's value is a weekday name and
  // doesn't populate the date field at all.
  private initialDateValue(type: string): string {
    if (!this.editingTemplate || type === 'DayOfWeek') return '';
    if (type === 'Manual') return this.editingTemplate.assignmentValue || '';
    // DayOfYear is stored as 'dd.MM' with no year - default to the current year for the date input.
    const [dd, mm] = (this.editingTemplate.assignmentValue || '').split('.');
    return dd && mm ? `${new Date().getFullYear()}-${mm}-${dd}` : '';
  }

  get slots() {
    return this.form.get('slots') as UntypedFormArray;
  }

  get isDayOfWeek(): boolean {
    return this.form.value.type === 'DayOfWeek';
  }

  get isRecurring(): boolean {
    return this.form.value.type === 'DayOfWeek' || this.form.value.type === 'DayOfYear';
  }

  selectedTypeHint(): string {
    return this.typeOptions.find(o => o.value === this.form.value.type)?.hint || '';
  }

  donorLabel(template: any): string {
    if (template.assignmentDateFrom && template.assignmentDateTo) {
      return `${template.name} (${this.formatDate(template.assignmentDateFrom)} – ${this.formatDate(template.assignmentDateTo)})`;
    }
    return `${template.name} (special schedule)`;
  }

  private formatDate(value: string): string {
    return value ? moment(value).format('YYYY-MM-DD') : '';
  }

  applyDonor(donorId: string) {
    const donor = this.donorTemplates.find(t => t.id === donorId);
    if (!donor) return;

    this.selectedDonorId = donorId;
    const slots = this.sortSlotsByTime(donor.slots || []);
    this.form.setControl('slots', this.fb.array(slots.map((slot: any) => this.createSlotGroupFromData(slot))));
  }

  private sortSlotsByTime(slots: any[]): any[] {
    return [...slots].sort((a, b) => this.timeToMinutes(a.Time) - this.timeToMinutes(b.Time));
  }

  private timeToMinutes(time: string): number {
    if (!time) return 0;
    const [hours, minutes] = time.split(':');
    return (Number(hours) || 0) * 60 + (Number(minutes) || 0);
  }

  private dictToRows(dict: { [key: string]: number }) {
    return Object.entries(dict || {}).map(([name, count]) => this.fb.group({
      name: [name, Validators.required],
      count: [count, [Validators.required, Validators.min(0)]]
    }));
  }

  private rowsToDict(rows: any[]): { [key: string]: number } {
    const dict: { [key: string]: number } = {};
    rows.forEach(row => {
      if (row.name) {
        dict[row.name] = Number(row.count) || 0;
      }
    });
    return dict;
  }

  createSlotGroup() {
    return this.fb.group({
      time: ['', Validators.required],
      reservedSeats: this.fb.array([]),
      startOffset: this.fb.array([])
    });
  }

  createSlotGroupFromData(slot: any) {
    return this.fb.group({
      time: [this.formatTimeForInput(slot.Time), Validators.required],
      reservedSeats: this.fb.array(this.dictToRows(slot.ReservedSeats)),
      startOffset: this.fb.array(this.dictToRows(slot.StartOffset))
    });
  }

  formatTimeForInput(time: string): string {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    return `${hours.padStart(2, '0')}:${minutes || '00'}`;
  }

  getReservedSeats(slotIndex: number) {
    return (this.slots.at(slotIndex).get('reservedSeats') as UntypedFormArray);
  }

  getStartOffset(slotIndex: number) {
    return (this.slots.at(slotIndex).get('startOffset') as UntypedFormArray);
  }

  addSlot() {
    this.slots.push(this.createSlotGroup());
  }

  removeSlot(index: number) {
    this.slots.removeAt(index);
  }

  addEntry(slotIndex: number, target: 'reservedSeats' | 'startOffset') {
    const array = target === 'reservedSeats' ? this.getReservedSeats(slotIndex) : this.getStartOffset(slotIndex);
    array.push(this.fb.group({
      name: ['', Validators.required],
      count: [0, [Validators.required, Validators.min(0)]]
    }));
  }

  removeEntry(slotIndex: number, target: 'reservedSeats' | 'startOffset', entryIndex: number) {
    const array = target === 'reservedSeats' ? this.getReservedSeats(slotIndex) : this.getStartOffset(slotIndex);
    array.removeAt(entryIndex);
  }

  save() {
    if (!this.form.valid || this.slots.length === 0) return;

    const name = (this.form.value.name || '').trim();
    const type = this.form.value.type;
    const timeSlots = this.slots.value
      .slice()
      .sort((a: any, b: any) => this.timeToMinutes(a.time) - this.timeToMinutes(b.time))
      .map((slot: any) => ({
        Time: slot.time,
        ReservedSeats: this.rowsToDict(slot.reservedSeats),
        StartOffset: this.rowsToDict(slot.startOffset)
      }));

    const options: { date?: string, dayOfWeek?: string, dateFrom?: string, dateTo?: string } = {};
    if (type === 'DayOfWeek') {
      options.dayOfWeek = this.form.value.dayOfWeek;
    } else {
      options.date = moment(this.form.value.date).format('YYYY-MM-DD');
    }
    if (this.isRecurring && this.form.value.hasPeriod) {
      options.dateFrom = this.form.value.dateFrom ? moment(this.form.value.dateFrom).format('YYYY-MM-DD') : undefined;
      options.dateTo = this.form.value.dateTo ? moment(this.form.value.dateTo).format('YYYY-MM-DD') : undefined;
    }

    this.error = null;
    this.saving = true;

    const request$ = this.editingTemplate
      ? this.backofficeService.updateRouteScheduleOverride(this.editingTemplate.id, name, timeSlots, type, options)
      : this.backofficeService.createRouteScheduleOverride(this.data.routeId, name, timeSlots, type, options);

    request$.subscribe({
      next: (response) => {
        this.saving = false;
        if (!response) {
          this.error = this.editingTemplate
            ? 'Could not save. A template with this name may already exist for this route.'
            : 'Could not create the override. A template with this name may already exist for this route.';
          return;
        }
        this.dialogRef.close({ success: true, template: response });
      },
      error: (err) => {
        this.saving = false;
        this.error = (typeof err?.error === 'string' && err.error) || 'Could not save the override schedule';
      }
    });
  }

  close() {
    this.dialogRef.close();
  }
}
